import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Icon,
  useTheme,
  Skeleton,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  alpha,
  AppBar,
  Toolbar,
} from "@mui/material";
import moment from "moment";
import {
  dashboardService,
  DashboardStats,
  SalesData,
  DashboardOrder,
  DashboardNotification,
  LowStockItem,
  SalesStatsResponse,
  SalesOverTimeResponse,
  RevenueOverTimeResponse,
  OrdersOverTimeResponse,
  TopPerformersResponse,
} from "../../services/dashboard.service";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { DateRangePicker } from "../../components/DateRangePicker/DateRangePicker";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import {
  StatCard,
  AreaChart,
  BarChart,
  DonutChart,
} from "../../components/Charts";
import {
  mockOrders,
  mockNotifications,
  mockLowStockItems,
} from "../../services/mockDashboardData";

// Section title component
const SectionTitle: React.FC<{ title: string }> = ({ title }) => {
  const theme = useTheme();

  return (
    <Box sx={{ mb: 3, mt: 4 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
      >
        {title}
      </Typography>
      <Divider />
    </Box>
  );
};

// Dashboard actions component
const DashboardActions: React.FC<{
  onRefresh: () => void;
  isLoading: boolean;
  isRefreshing: boolean;
  dateRange: { start: Date; end: Date };
  onDateRangeChange: (range: { start: Date; end: Date }) => void;
}> = ({ onRefresh, isLoading, isRefreshing, dateRange, onDateRangeChange }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
      }}
    >
      <DateRangePicker
        startDate={dateRange.start}
        endDate={dateRange.end}
        onChange={onDateRangeChange}
      />

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          bgcolor: theme.palette.background.paper,
          p: 0.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.02)}`,
        }}
      >
        {/* <Tooltip title="View Reports">
          <IconButton
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Icon>assessment</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Export Data">
          <IconButton
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Icon>download</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Print Report">
          <IconButton
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Icon>print</Icon>
          </IconButton>
        </Tooltip>*/}
        {/* <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />  */}
        <Tooltip title={isRefreshing ? "Refreshing..." : "Refresh dashboard"}>
          <span>
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={isLoading || isRefreshing}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <Icon>{isRefreshing ? "sync" : "refresh"}</Icon>
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export const DashboardV2: React.FC = () => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [salesStats, setSalesStats] = useState<
    SalesStatsResponse["data"] | null
  >(null);
  const [salesOverTime, setSalesOverTime] = useState<
    SalesOverTimeResponse["data"]["sales_data"]
  >([]);
  const [revenueOverTime, setRevenueOverTime] = useState<
    RevenueOverTimeResponse["data"]["revenue_data"]
  >([]);
  const [ordersOverTime, setOrdersOverTime] = useState<
    OrdersOverTimeResponse["data"]["orders_data"]
  >([]);
  const [topPerformers, setTopPerformers] = useState<
    TopPerformersResponse["data"] | null
  >(null);
  const [recentOrders, setRecentOrders] = useState<DashboardOrder[]>([]);
  const [notifications, setNotifications] = useState<DashboardNotification[]>(
    []
  );
  const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([]);
  const [dateRange, setDateRange] = useState({
    start: moment().subtract(7, "days").toDate(),
    end: moment().toDate(),
  });

  // Store ID - this should come from your auth context or app state
  const storeId = "dea36caa-e645-45e6-8614-2fdf23d69466"; // Replace with actual store ID

  const fetchSalesStats = async () => {
    try {
      const data = await dashboardService.getSalesStats(
        dateRange.start,
        dateRange.end,
        storeId
      );
      setSalesStats(data);
    } catch (err) {
      console.error("Error fetching sales stats:", err);
      throw err;
    }
  };

  const fetchSalesOverTime = async () => {
    try {
      const data = await dashboardService.getSalesOverTime(
        storeId,
        dateRange.start,
        dateRange.end
      );
      setSalesOverTime(data);
    } catch (err) {
      console.error("Error fetching sales over time:", err);
      throw err;
    }
  };

  const fetchRevenueOverTime = async () => {
    try {
      const data = await dashboardService.getRevenueOverTime(
        storeId,
        dateRange.start,
        dateRange.end
      );
      setRevenueOverTime(data);
    } catch (err) {
      console.error("Error fetching revenue over time:", err);
      throw err;
    }
  };

  const fetchOrdersOverTime = async () => {
    try {
      const data = await dashboardService.getOrdersOverTime(
        storeId,
        dateRange.start,
        dateRange.end
      );
      setOrdersOverTime(data);
    } catch (err) {
      console.error("Error fetching orders over time:", err);
      throw err;
    }
  };

  const fetchTopPerformers = async () => {
    try {
      const data = await dashboardService.getTopPerformers(
        storeId,
        dateRange.start,
        dateRange.end
      );
      setTopPerformers(data);
    } catch (err) {
      console.error("Error fetching top performers:", err);
      throw err;
    }
  };

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      // Fetch data from the APIs
      await Promise.all([
        fetchSalesStats(),
        fetchSalesOverTime(),
        fetchRevenueOverTime(),
        fetchOrdersOverTime(),
        fetchTopPerformers(),
      ]);

      // Use mock data for other sections until we implement their APIs
      setRecentOrders(mockOrders);
      setNotifications(mockNotifications);
      setLowStockItems(mockLowStockItems);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load dashboard data";
      setError(message);
      if (isRefresh) {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Refetch data when date range changes
  useEffect(() => {
    if (!isLoading) {
      Promise.all([
        fetchSalesStats(),
        fetchSalesOverTime(),
        fetchRevenueOverTime(),
        fetchOrdersOverTime(),
        fetchTopPerformers(),
      ]).catch((err) => {
        console.error("Error refreshing data:", err);
        toast.error("Failed to refresh dashboard data");
      });
    }
  }, [dateRange]);

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "BHD",
    }).format(value);
  };

  // Prepare data for stat cards
  const statCards = salesStats
    ? [
      {
        title: "Total Sales",
        value: formatCurrency(salesStats.total_sales),
        icon: "payments",
        color: theme.palette.success.main,
        trend: {
          value: 12.5, // This should come from the API in a real implementation
          isPositive: true,
        },
      },
      {
        title: "Total Orders",
        value: salesStats.total_orders,
        icon: "shopping_cart",
        color: theme.palette.info.main,
        trend: {
          value: 8.2, // This should come from the API in a real implementation
          isPositive: true,
        },
      },
      {
        title: "Average Sale",
        value: formatCurrency(salesStats.average_sales),
        icon: "trending_up",
        color: theme.palette.warning.main,
        trend: {
          value: 3.1, // This should come from the API in a real implementation
          isPositive: true,
        },
      },
      {
        title: "Total Revenue",
        value: formatCurrency(salesStats.total_revenue),
        icon: "account_balance",
        color: theme.palette.primary.main,
        trend: {
          value: 15.7, // This should come from the API in a real implementation
          isPositive: true,
        },
      },
    ]
    : [];

  // Transform sales over time data for the AreaChart component
  const salesOverTimeData = salesOverTime.map((item) => ({
    date: moment(item.date).format("MMM D"),
    sales: item.total_sales,
  }));

  // Transform revenue over time data for the AreaChart component
  const revenueOverTimeData = revenueOverTime.map(
    (item: { date: string; total_revenue: number }) => ({
      date: moment(item.date).format("MMM D"),
      revenue: item.total_revenue,
    })
  );

  // Transform orders over time data for the AreaChart component
  const ordersOverTimeData = ordersOverTime.map(
    (item: { date: string; total_orders: number }) => ({
      date: moment(item.date).format("MMM D"),
      orders: item.total_orders,
    })
  );

  // Transform top selling items data for the BarChart component
  const topSellingItemsData =
    topPerformers?.top_selling_items.map((item) => ({
      name: item.name,
      value: item.count,
    })) || [];

  // Transform top revenue items data for the BarChart component
  const topRevenueItemsData =
    topPerformers?.top_revenue_items.map((item) => ({
      name: item.name,
      value: item.revenue,
    })) || [];

  // Transform top cashiers data for the BarChart component
  const topCashiersData =
    topPerformers?.top_cashiers.map((item) => ({
      name: item.name,
      value: item.total_orders,
    })) || [];

  // Calculate total sales for the subtitle
  const totalSales = salesOverTime.reduce(
    (sum: number, item: { total_sales: number }) => sum + item.total_sales,
    0
  );

  // Calculate total revenue for the subtitle
  const totalRevenue = revenueOverTime.reduce(
    (sum: number, item: { total_revenue: number }) => sum + item.total_revenue,
    0
  );

  // Calculate total orders for the subtitle
  const totalOrders = ordersOverTime.reduce(
    (sum: number, item: { total_orders: number }) => sum + item.total_orders,
    0
  );

  // Calculate payment method distribution for donut chart
  const paymentMethodData = recentOrders.reduce((acc, order) => {
    const method = order.payment_method.toLowerCase();
    acc[method] = (acc[method] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const donutChartData = Object.entries(paymentMethodData).map(
    ([label, value]) => ({
      name: label.charAt(0).toUpperCase() + label.slice(1),
      value,
    })
  );

  // Calculate hourly order distribution for bar chart
  const hourlyOrderData = recentOrders.reduce((acc, order) => {
    const hour = new Date(order.order_date).getHours();
    acc[hour] = (acc[hour] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);

  const barChartData = Array.from({ length: 24 }, (_, hour) => ({
    hour: `${hour.toString().padStart(2, "0")}:00`,
    orders: hourlyOrderData[hour] || 0,
  }));

  if (error && !isRefreshing) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          variant="filled"
          action={
            <IconButton color="inherit" size="small" onClick={handleRefresh}>
              <Icon>refresh</Icon>
            </IconButton>
          }
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
        <EmptyState
          icon="error_outline"
          title="Failed to Load Dashboard"
          message="Please try refreshing the page or contact support if the problem persists."
          height={400}
        />
      </Box>
    );
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{ p: 3 }}
    >
      <PageHeader
        title="Business Dashboard"
        subtitle="Monitor your business performance and insights"
        icon="insights"
        actions={
          <DashboardActions
            onRefresh={handleRefresh}
            isLoading={isLoading || isRefreshing}
            isRefreshing={isRefreshing}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
        }
      />

      {/* Stats Cards */}
      <Grid
        container
        spacing={{
          xs: 2,
          sm: 2,
          md: 3,
        }}
        sx={{ mb: { xs: 3, sm: 4 } }}
      >
        {(isLoading ? Array(4).fill(null) : statCards).map((stat, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            lg={3}
            key={stat?.title || index}
            sx={{
              "& .MuiCard-root": {
                minHeight: { xs: "140px", sm: "160px" },
              },
            }}
          >
            {isLoading ? (
              <Skeleton
                variant="rectangular"
                height={140}
                sx={{ borderRadius: 2 }}
              />
            ) : (
              <StatCard
                {...stat}
                isLoading={isRefreshing}
                delay={index * 0.1}
              />
            )}
          </Grid>
        ))}
      </Grid>

      {/* Sales Analytics Section */}
      <SectionTitle title="Sales Analytics" />
      <Grid
        container
        spacing={{
          xs: 2,
          sm: 2,
          md: 3,
        }}
        sx={{ mb: { xs: 3, sm: 4 } }}
      >
        <Grid item xs={12} lg={7}>
          <AreaChart
            title="Sales Trend"
            icon="show_chart"
            data={salesOverTimeData}
            xAxisKey="date"
            yAxisKey="sales"
            height={400}
            subtitle={`Total Sales: ${formatCurrency(totalSales)}`}
            isLoading={isLoading || isRefreshing}
            emptyStateMessage="Start making sales to see your business trends here."
            formatYAxis={(value) => formatCurrency(value)}
            formatTooltip={(value) => formatCurrency(value)}
            delay={0.2}
          />
        </Grid>
        <Grid item xs={12} lg={5}>
          <BarChart
            title="Top Selling Items"
            icon="inventory_2"
            data={topSellingItemsData}
            height={400}
            subtitle={`${topSellingItemsData.length} Items`}
            isLoading={isLoading || isRefreshing}
            emptyStateMessage="No top selling items data available yet."
            delay={0.3}
            maxItems={5}
            color="#0891B2"
          />
        </Grid>
      </Grid>

      {/* Revenue Analytics Section */}
      <SectionTitle title="Revenue Analytics" />
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={7}>
          <AreaChart
            title="Revenue Trend"
            icon="trending_up"
            data={revenueOverTimeData}
            xAxisKey="date"
            yAxisKey="revenue"
            height={400}
            subtitle={`Total Revenue: ${formatCurrency(totalRevenue)}`}
            isLoading={isLoading || isRefreshing}
            emptyStateMessage="Start generating revenue to see your business trends here."
            formatYAxis={(value) => formatCurrency(value)}
            formatTooltip={(value) => formatCurrency(value)}
            delay={0.4}
          />
        </Grid>
        <Grid item xs={12} lg={5}>
          <BarChart
            title="Top Revenue Items"
            icon="payments"
            data={topRevenueItemsData}
            height={400}
            subtitle={`${topRevenueItemsData.length} Items`}
            isLoading={isLoading || isRefreshing}
            emptyStateMessage="No top revenue items data available yet."
            delay={0.5}
            maxItems={5}
            formatValue={(value) => value.toFixed(0)}
            valuePrefix="BHD "
            valueSuffix=""
            color="#0891B2"
          />
        </Grid>
      </Grid>

      {/* Orders Analytics Section */}
      <SectionTitle title="Orders Analytics" />
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={7}>
          <AreaChart
            title="Orders Trend"
            icon="shopping_cart"
            data={ordersOverTimeData}
            xAxisKey="date"
            yAxisKey="orders"
            height={400}
            subtitle={`Total Orders: ${totalOrders}`}
            isLoading={isLoading || isRefreshing}
            emptyStateMessage="Start receiving orders to see your order trends here."
            delay={0.6}
          />
        </Grid>
        <Grid item xs={12} lg={5}>
          <BarChart
            title="Top Cashiers"
            icon="person"
            data={topCashiersData}
            height={400}
            subtitle={`${topCashiersData.length} Cashiers`}
            isLoading={isLoading || isRefreshing}
            emptyStateMessage="No top cashiers data available yet."
            delay={0.7}
            maxItems={5}
            color="#0891B2"
          />
        </Grid>
      </Grid>

      {/* Last Updated Info - At the bottom */}
      <Box
        sx={{
          mt: 4,
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "text.secondary",
          fontSize: "0.875rem",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Icon style={{ fontSize: "1.1rem" }}>update</Icon>
          Last updated: {moment().format("MMM D, YYYY h:mm A")}
        </Box>
      </Box>
    </Box>
  );
};
